<template>
  <div>
    <span class="flex mb-8">
      <h3 class="text-h4 mb-8">Matches your job</h3>
      <img v-if="isBasic" src="../../images/applications/unlock-premium.svg" alt="Upgrade To Premium" class="ml-8 mb-8" />
      <poppins-badge
        v-if="!isBasic && matchStrengthDetails"
        class="max-h-[28px] ml-8"
        :badgeContent="matchStrengthDetails.text"
        :variant="matchStrengthDetails.variant"
      />
    </span>
    <div v-if="isBasic">
      <li class="text-grey-body flex text-caption items-center mb-8">
        <img src="../../images/applications/basic-match.svg" alt="Upgrade To Premium" class="mr-8" />
        <span>See what skills this sitter has that match your job requirements</span>
      </li>
      <poppins-button
        value="Upgrade To Premium"
        variant="outlined"
        class="w-full xl:w-auto mt-16"
        :href="upgradeUrl"
        data-testid="upgrade-btn"
        @click="upgradeClicked"
      />
    </div>
    <div v-else>
      <div>
        <div class="flex items-center gap-8">
          <warning-outline-icon :width="16" :height="16" color="#6E6F74" class="rotate-180 shrink-0" />
          <p class="text-caption text-grey-body">
            This score shows how well the sitter’s profile matches the job criteria from your job post. <b>Strong</b> = 80%+,
            <b>Moderate</b> = 50–79%, <b>Partial</b> = under 50%.
          </p>
        </div>
        <progress-bar
          :max="100"
          :value="matchPercent"
          content=""
          color="#B84D8D"
          customClasses="mt-8 !h-8 overflow-hidden rounded-xl"
        />
      </div>
      <accordion-list :items="accordionItems" class="mt-16">
        <template
          v-for="category in categoriesWithAccordionContent"
          :key="category.kind"
          #[`content-${getCategoryId(category.kind)}`]
        >
          <div>
            <tag-label-group :items="getCategoryItems(category)" />
          </div>
        </template>
      </accordion-list>
    </div>
  </div>
</template>

<script lang="ts">
import WarningOutlineIcon from '@/graphics/icons/WarningOutlineIcon.vue';
import TickIcon from '@/graphics/icons/TickIcon.vue';
import CloseIcon from '@/graphics/icons/CloseIcon.vue';
import ProgressBar from '@/components/progress_bar/ProgressBar.vue';
import AccordionList from '@/components/accordion_list/AccordionList.vue';
import TagLabelGroup from '@/components/tag_label_group/TagLabelGroup.vue';
import TickCircleIcon from '@/graphics/icons/TickCircleIcon.vue';
import TickCircleOutlineIcon from '@/graphics/icons/TickCircleOutlineIcon.vue';
import ClearIcon from '@/graphics/icons/ClearIcon.vue';
import PoppinsButton from '@/components/button/Button.vue';
import PoppinsBadge from '@/components/badge/Badge.vue';
import { sendSegmentEvent } from '../../lib/segment';
import { setRecentlyViewedProviderId } from '@/lib/recentlyViewedProfile';
import { markComponentRaw } from '@/helpers/markComponentRaw';
import { ApplicationDetails, MatchCategory } from '@/store/modules/applications/types';

export default {
  components: {
    WarningOutlineIcon,
    ProgressBar,
    AccordionList,
    TagLabelGroup,
    PoppinsButton,
    PoppinsBadge,
  },
  props: {
    applicationDetails: {
      type: Object as () => ApplicationDetails,
      required: false,
      default: () => ({}),
    },
    isBasic: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    matchDetails(): any {
      return this.applicationDetails.matchSummary;
    },
    matchStrengthDetails(): { text: string; variant: string } | null {
      if (this.isBasic) return null;
      switch (this.matchDetails?.strength) {
        case 'STRONG':
          return { text: 'Strong fit', variant: 'strong' };
        case 'MODERATE':
          return { text: 'Moderate fit', variant: 'moderate' };
        case 'PARTIAL':
          return { text: 'Partial fit', variant: 'subdued' };
        default:
          return null;
      }
    },
    matchPercent(): number {
      return this.matchDetails?.percent || 0;
    },
    categories(): MatchCategory[] {
      const order = ['AGE_GROUP', 'RATE', 'LOCATION', 'SKILLS_SERVICES'];
      const categories = this.matchDetails?.categories || [];

      return [...categories].sort((a, b) => order.indexOf(a.kind) - order.indexOf(b.kind));
    },
    categoriesWithAccordionContent(): MatchCategory[] {
      return this.categories.filter((category) => category?.matched?.length || category?.unmatched?.length);
    },
    upgradeUrl(): string {
      return '/upgrade/pricing';
    },
    accordionItems(): any[] {
      return this.categories.map((category) => {
        return {
          id: this.getCategoryId(category.kind),
          label: this.getCategoryLabel(category.kind),
          sublabel: category.headline,
          icon: this.getIconForStrength(category.strength),
          iconColor: category.strength === 'PARTIAL' ? '#6E6F74' : '#B84D8D',
        };
      });
    },
  },
  methods: {
    upgradeClicked(): void {
      setRecentlyViewedProviderId((this as any).applicationDetails?.sitter?.id);
      sendSegmentEvent('Applicant_MatchesJob_UpgradePricing', {});
    },
    getCategoryId(kind: string): string {
      return kind.toLowerCase().replace(/_/g, '-');
    },
    getCategoryLabel(kind: string): string {
      const labels = {
        AGE_GROUP: 'Age group experience',
        SKILLS_SERVICES: 'Skills & services',
        RATE: 'Rate range',
        LOCATION: 'Location',
      };
      return labels[kind] || kind;
    },
    getIconForStrength(strength: string): any {
      if (strength === 'STRONG') return markComponentRaw(TickCircleIcon);
      if (strength === 'MODERATE') return markComponentRaw(TickCircleOutlineIcon);
      return markComponentRaw(ClearIcon);
    },
    getCategoryItems(category: MatchCategory): Array<{ icon: any; text: string; muted?: boolean }> {
      const items: Array<{ icon: any; text: string; muted?: boolean }> = [];

      if (category.matched) {
        items.push(
          ...category.matched.map((item) => ({
            icon: markComponentRaw(TickIcon),
            text: item.label,
          }))
        );
      }

      if (category.unmatched) {
        items.push(
          ...category.unmatched.map((item) => ({
            icon: markComponentRaw(CloseIcon),
            text: item.label,
            muted: true,
          }))
        );
      }

      return items;
    },
  },
};
</script>
